import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Badge, Button } from '@/components/ui'
import { useAuth } from '@/contexts/AuthContext'
import {
  FileText,
  CreditCard,
  QrCode,
  Image,
  Mail,
  Hash,
  Shield,
  Wrench,
  Sparkles,
  Clock,
  Users,
  Eye,
  Send,
  ShoppingBag,
  MessageCircle,
  Copy,
  Check,
} from 'lucide-react'
import { tools } from './Tools'
import { useRecentTools } from '@/hooks/useRecentTools'
import { CONTACT, telegramDM } from '@/config/contact'

interface ShopItem {
  id: string
  name: string
  description: string
  price: string
  image?: string
  telegramText: string
}

export function Home() {
  const { recentHrefs } = useRecentTools()
  const { isAuthenticated } = useAuth()
  const [visitors, setVisitors] = useState({ totalUnique: 0, todayVisitors: 0, totalVisits: 0 })
  const [shopItems, setShopItems] = useState<ShopItem[]>([])
  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  // Get popular tools dynamically (limit to 6)
  const popularTools = useMemo(() => {
    return tools.filter(tool => tool.popular).slice(0, 6)
  }, [])

  const totalTools = tools.length
  const categories = [...new Set(tools.map(t => t.category))].length

  // Get recent tools with full data
  const recentTools = recentHrefs
    .map(href => tools.find(t => t.href === href))
    .filter(Boolean) as typeof tools

  // Track visitor and fetch stats
  useEffect(() => {
    fetch('/api/visitors/track', {
      method: 'POST',
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => setVisitors(data))
      .catch(() => {})

    fetch('/api/shop/products', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setShopItems(data) })
      .catch(() => {})
  }, [])

  const copyTelegramLink = (item: ShopItem) => {
    navigator.clipboard.writeText(telegramDM(item.telegramText))
    setCopiedItem(item.id)
    setTimeout(() => setCopiedItem(null), 2000)
  }

  return (
    <div className="space-y-4">
      {/* Welcome Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <img
            src="/images/logo-64.png"
            alt="SCB|PH Logo"
            className="h-16 w-16"
          />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-primary glow-text">{'>'}</span> Welcome to SCB|PH Terminal
            </h1>
            <p className="text-muted-foreground">
              Your comprehensive toolkit for document generation, identity tools, and utilities.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              <Wrench className="h-3.5 w-3.5" /> Available Tools
            </CardDescription>
            <CardTitle className="text-3xl text-primary">{totalTools}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5" /> Categories
            </CardDescription>
            <CardTitle className="text-3xl text-primary">{categories}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Total Visitors
            </CardDescription>
            <CardTitle className="text-3xl text-primary">{visitors.totalUnique}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" /> Today
            </CardDescription>
            <CardTitle className="text-3xl text-green-500">{visitors.todayVisitors}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Recently Used Tools */}
      {recentTools.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-xl font-semibold">Recently Used</h2>
          </div>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            {recentTools.map((tool) => {
              const Icon = tool.icon
              return (
                <Link key={tool.href} to={tool.href}>
                  <Card className="glass h-full transition-all hover:border-primary/50 hover:shadow-glow cursor-pointer">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${
                        tool.category === 'Special Tools' ? 'bg-gradient-to-br from-primary/20 to-purple-500/20' :
                        tool.category === 'Encryptors' ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' :
                        'bg-primary/10'
                      }`}>
                        <Icon className={`h-4 w-4 ${
                          tool.category === 'Encryptors' ? 'text-orange-500' : 'text-primary'
                        }`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{tool.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{tool.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Quick Access */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Quick Access</h2>
          <Link
            to="/tools"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            <Wrench className="h-4 w-4" />
            View all tools
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {popularTools.map((tool) => {
            const Icon = tool.icon
            return (
              <Link key={tool.href} to={tool.href}>
                <Card className="glass h-full transition-all hover:border-primary/50 hover:shadow-glow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tool.category === 'Special Tools' ? 'bg-gradient-to-br from-primary/20 to-purple-500/20' : 'bg-primary/10'}`}>
                        <Icon className={`h-5 w-5 ${tool.category === 'Special Tools' ? 'text-primary' : 'text-primary'}`} />
                      </div>
                      <div className="flex gap-1">
                        {tool.authOnly && !isAuthenticated && (
                          <span className="text-lg" title="Login required">🔒</span>
                        )}
                        {tool.category === 'Special Tools' && (
                          <Badge variant="secondary" className="text-xs">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Special
                          </Badge>
                        )}
                        {tool.popular && (
                          <Badge variant="secondary" className="text-xs">
                            Popular
                          </Badge>
                        )}
                        {tool.vip && (
                          <Badge className="bg-gradient-to-r from-yellow-500 to-amber-600">
                            VIP
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-base">{tool.title}</CardTitle>
                    <CardDescription>{tool.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            )
          })}
        </div>
      </div>

      {/* Vault Promo */}
      <Card className="glass border-primary/30">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/20">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">Unlock the Vault</h3>
            <p className="text-sm text-muted-foreground">
              Get access to premium tools and exclusive features with membership.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Telegram Portal */}
      <a
        href={CONTACT.telegram.portal}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <Card className="glass border-[#0088cc]/30 transition-all hover:border-[#0088cc]/60 hover:shadow-[0_0_20px_rgba(0,136,204,0.15)] cursor-pointer">
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0088cc]/20">
              <Send className="h-6 w-6 text-[#0088cc]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Telegram Portal</h3>
              <p className="text-sm text-muted-foreground">
                Join our Telegram community for updates, support, and exclusive content.
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2 rounded-md bg-[#0088cc] px-4 py-2 text-sm font-medium text-white">
              <Send className="h-4 w-4" />
              Join Now
            </div>
          </CardContent>
        </Card>
      </a>

      {/* Shop */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Shop</h2>
          <Badge variant="secondary" className="text-xs">Services & Products</Badge>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {shopItems.map((item) => (
            <Card key={item.id} className="glass transition-all hover:border-primary/50 overflow-hidden">
              {item.image && (
                <div className="w-full h-36 overflow-hidden border-b border-border">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
              )}
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base">{item.name}</CardTitle>
                  <Badge variant="secondary" className="text-xs shrink-0">{item.price}</Badge>
                </div>
                <CardDescription>{item.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <a
                    href={telegramDM(item.telegramText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" size="sm" className="w-full">
                      <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                      Contact via Telegram
                    </Button>
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyTelegramLink(item)}
                    title="Copy Telegram link"
                  >
                    {copiedItem === item.id ? (
                      <Check className="h-3.5 w-3.5 text-green-400" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact card */}
        <Card className="glass border-primary/20">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                <MessageCircle className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Need something custom?</h3>
                <p className="text-sm text-muted-foreground">
                  Have a specific request or need a custom tool? Reach out directly and let's discuss your needs.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <a
                  href={telegramDM('Hi, I have a custom request. Can we discuss?')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="sm">
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    Message on Telegram
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
